/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import { DENIAL_RESOLVE_ACTION_LABELS } from 'components/lab/billingDenial/consts'
import { WriteOffReasonField } from 'components/lab/billingPayment/WriteOffReasonField'
import { formatYyyyMmDd } from 'components/lab/billingPayment/ServiceLineRow'
import { toNum } from 'components/lab/orderEdit/helpers'
import React from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { useResolveDenialMutation } from 'store/services/billingDenials'
import { BillingDenialDto, BillingDenialResolveAction } from 'store/types'
import { isWriteOffReason, OTHER_WRITE_OFF_REASON } from 'ts-common/utils/writeOffReasons.utils'

type Props = {
  denial: BillingDenialDto
  action: BillingDenialResolveAction
  onClose: () => void
}

type Amounts = Record<string, { patientAmount: string; writeOffAmount: string }>

const money = (value: number) => `$${value.toFixed(2)}`

// Starts from the server's suggestion; the biller corrects per line.
const initialAmounts = (denial: BillingDenialDto, action: BillingDenialResolveAction): Amounts =>
  Object.fromEntries((denial.suggestedResolutions?.[action] ?? []).map((line) => [
    line.lineId,
    { patientAmount: line.patientAmount.toFixed(2), writeOffAmount: line.writeOffAmount.toFixed(2) },
  ]))

/**
 * Resolving a denial is when money moves: the write-off and/or the patient
 * responsibility post as a $0 payment from the denying payer.
 */
export const ResolveDenialModal = ({ denial, action, onClose }: Props) => {
  const [resolveDenial, { isLoading }] = useResolveDenialMutation()
  const [amounts, setAmounts] = React.useState<Amounts>(() => initialAmounts(denial, action))
  // Starts from the reason an ERA posting of the denial's CARCs would get.
  const [writeOffReason, setWriteOffReason] = React.useState(denial.suggestedWriteOffReason ?? '')
  const [writeOffNote, setWriteOffNote] = React.useState('')
  const [showErrors, setShowErrors] = React.useState(false)

  const isPatient = action === 'PATIENT'
  // Settled some other way: nothing to move, the dialog only closes the denial.
  const nothingOpen = denial.openPlanBalance !== undefined && denial.openPlanBalance <= 0
  const totalPatient = denial.lines.reduce((sum, line) => sum + toNum(amounts[line.id]?.patientAmount), 0)
  const totalWriteOff = denial.lines.reduce((sum, line) => sum + toNum(amounts[line.id]?.writeOffAmount), 0)
  const reasonMissing = totalWriteOff > 0
    && (!isWriteOffReason(writeOffReason) || (writeOffReason === OTHER_WRITE_OFF_REASON && !writeOffNote.trim()))

  const setAmount = (lineId: string, field: 'patientAmount' | 'writeOffAmount', value: string) =>
    setAmounts((current) => ({ ...current, [lineId]: { ...current[lineId], [field]: value } }))

  const onSubmit = async () => {
    if (reasonMissing) {
      setShowErrors(true)
      return
    }

    try {
      await resolveDenial({
        patientId: denial.patientId,
        denialId: denial.id,
        action,
        lines: denial.lines.map((line) => ({
          lineId: line.id,
          patientAmount: isPatient ? Number(toNum(amounts[line.id]?.patientAmount).toFixed(2)) : 0,
          writeOffAmount: Number(toNum(amounts[line.id]?.writeOffAmount).toFixed(2)),
        })),
        writeOffReason: totalWriteOff > 0 && isWriteOffReason(writeOffReason) ? writeOffReason : null,
        writeOffNote: writeOffNote.trim() || undefined,
      }).unwrap()

      Toast({ type: 'success', label: nothingOpen ? 'Denial closed' : 'Denial resolved' })
      onClose()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to resolve the denial' })
    }
  }

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 18 }}>
          {DENIAL_RESOLVE_ACTION_LABELS[action]} — {denial.payerName || 'payer'} denial
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {nothingOpen && (
          <Alert variant="info" className="p-2" css={{ fontSize: 12 }}>
            Nothing left to settle on this charge: its plan balance is already $0.00. Closing resolves the
            denial without posting anything.
          </Alert>
        )}
        {!nothingOpen && isPatient && denial.patientAmountGoesToPayerName && (
          <Alert variant="info" className="p-2" css={{ fontSize: 12 }}>
            This charge has {denial.patientAmountGoesToPayerName} after the denying payer, so the patient amount
            goes to {denial.patientAmountGoesToPayerName} first, the same as posted patient responsibility.
          </Alert>
        )}
        <table className="table table-sm mb-2">
          <thead>
            <tr css={{ '& > th': { fontWeight: 500 } }}>
              <th>CPT</th>
              <th>DOS</th>
              <th>Charged</th>
              <th>Payer patient resp.</th>
              {isPatient && <th>To patient</th>}
              <th>Write-off</th>
            </tr>
          </thead>
          <tbody>
            {denial.lines.map((line) => (
              <tr key={line.id}>
                <td>{line.cptCode}</td>
                <td>{formatYyyyMmDd(line.startDos)}</td>
                <td>{money(line.charged)}</td>
                <td>{money(line.patientResponsibility)}</td>
                {isPatient && (
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      aria-label={`Patient amount for CPT ${line.cptCode}`}
                      value={amounts[line.id]?.patientAmount ?? ''}
                      disabled={nothingOpen}
                      onChange={(e) => setAmount(line.id, 'patientAmount', e.target.value)}
                    />
                  </td>
                )}
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    aria-label={`Write-off for CPT ${line.cptCode}`}
                    value={amounts[line.id]?.writeOffAmount ?? ''}
                    disabled={nothingOpen}
                    onChange={(e) => setAmount(line.id, 'writeOffAmount', e.target.value)}
                  />
                </td>
              </tr>
            ))}
            <tr css={{ fontWeight: 600 }}>
              <td colSpan={4}>Total</td>
              {isPatient && <td>{money(totalPatient)}</td>}
              <td>{money(totalWriteOff)}</td>
            </tr>
          </tbody>
        </table>
        {totalWriteOff > 0 && (
          <div>
            <label className="textGray mb-0">Write-off reason</label>
            <WriteOffReasonField
              compact={false}
              writeOff={totalWriteOff}
              reason={writeOffReason}
              note={writeOffNote}
              onReasonChange={setWriteOffReason}
              onNoteChange={setWriteOffNote}
              hasError={showErrors && reasonMissing}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            isLoading
            || (!nothingOpen && (totalPatient + totalWriteOff <= 0 || (isPatient && totalPatient <= 0)))
          }
          onClick={onSubmit}>
          {nothingOpen ? 'Close Denial' : DENIAL_RESOLVE_ACTION_LABELS[action]}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
